/**
 * Generateur de modeles Excel pour la migration Odoo.
 * Quatre modeles, aux formats EXACTS des exports Odoo francais :
 *  - Ecritures comptables (account.move.line)
 *  - Factures (account.move)
 *  - Plan comptable (account.account)
 *  - Contacts (res.partner)
 *
 * Les en-tetes correspondent aux colonnes relues par les importers backend
 * (odoo_migration/services/*). Donnees d'exemple fictives.
 */
import ExcelJS from "exceljs";

const GREEN = "FF009688";
const LIGHT_BG = "FFF5F5F5";

/* ── Helpers ───────────────────────────────────────────────────────── */

const styleHeader = (worksheet) => {
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: GREEN },
  };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
  headerRow.height = 25;
  headerRow.eachCell((cell) => {
    cell.border = {
      top: { style: "thin", color: { argb: "FFD3D3D3" } },
      bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
      left: { style: "thin", color: { argb: "FFD3D3D3" } },
      right: { style: "thin", color: { argb: "FFD3D3D3" } },
    };
  });
};

const addExampleRows = (worksheet, rows) => {
  rows.forEach((row) => worksheet.addRow(row));
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber <= 1) return;
    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin", color: { argb: "FFD3D3D3" } },
        bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
        left: { style: "thin", color: { argb: "FFD3D3D3" } },
        right: { style: "thin", color: { argb: "FFD3D3D3" } },
      };
      cell.alignment = { vertical: "middle" };
    });
    if (rowNumber % 2 === 0) {
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: LIGHT_BG } };
    }
  });
};

const addInstructionsSheet = (workbook, title, instructions) => {
  const sheet = workbook.addWorksheet("Instructions");
  sheet.columns = [{ width: 95 }];
  instructions.forEach((line, i) => {
    const row = sheet.getRow(i + 1);
    row.getCell(1).value = line.text;
    if (line.bold) {
      row.getCell(1).font = {
        bold: true,
        size: line.size || 11,
        color: line.color ? { argb: `FF${line.color}` } : undefined,
      };
    }
    row.getCell(1).alignment = { vertical: "top", wrapText: true };
    row.height = 20;
  });
};

const downloadWorkbook = async (workbook, filename) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
};

/* ── 1. Ecritures comptables (account.move.line) ──────────────────── */

export const generateMoveLinesTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Ecritures");

  ws.columns = [
    { header: "Date de facturation", key: "date_facturation", width: 20 },
    { header: "Date", key: "date", width: 14 },
    { header: "Journal", key: "journal", width: 20 },
    { header: "Numéro", key: "numero", width: 18 },
    { header: "Compte", key: "compte", width: 38 },
    { header: "Partenaire", key: "partenaire", width: 22 },
    { header: "Libellé", key: "libelle", width: 20 },
    { header: "Débit", key: "debit", width: 14 },
    { header: "Crédit", key: "credit", width: 14 },
    { header: "Échéance", key: "echeance", width: 14 },
    { header: "Solde", key: "solde", width: 14 },
  ];

  styleHeader(ws);

  addExampleRows(ws, [
    ["2026-01-15", "2026-01-15", "Ventes", "INV/2026/0001", "411100 Clients", "Client Test", "INV/2026/0001", 100000, 0, "2026-02-15", 100000],
    ["2026-01-15", "2026-01-15", "Ventes", "INV/2026/0001", "701100 Vente de marchandises dans la région", "Client Test", "Produit A", 0, 60000, null, -60000],
    ["2026-01-15", "2026-01-15", "Ventes", "INV/2026/0001", "701100 Vente de marchandises dans la région", "Client Test", "Produit B", 0, 40000, null, -40000],
    ["2026-01-20", "2026-01-20", "Achats", "BILL/2026/0001", "601100 Achats de marchandises", "Fournisseur Test", "Achat janvier", 50000, 0, null, 50000],
    ["2026-01-20", "2026-01-20", "Achats", "BILL/2026/0001", "401100 Fournisseurs", "Fournisseur Test", "BILL/2026/0001", 0, 50000, "2026-02-20", 0],
    ["2026-01-25", "2026-01-25", "Opérations diverses", "MISC/2026/0001", "571100 Argent liquide en monnaie nationale", null, "Retrait caisse", 25000, 0, null, 25000],
    ["2026-01-25", "2026-01-25", "Opérations diverses", "MISC/2026/0001", "521001 Banque", null, "Retrait caisse", 0, 25000, null, -25000],
  ]);

  addInstructionsSheet(workbook, "Ecritures", [
    { text: "MODELE IMPORT ECRITURES COMPTABLES ODOO (account.move.line)", bold: true, size: 16, color: "009688" },
    { text: "" },
    { text: "FICHIER OBLIGATOIRE - il contient toute votre comptabilite.", bold: true, size: 12, color: "c0392b" },
    { text: "" },
    { text: "COLONNES (format export Odoo francais) :", bold: true, size: 12 },
    { text: "1. Date              - Date de l'ecriture" },
    { text: "2. Journal           - Nom du journal Odoo (informatif)" },
    { text: "3. Numéro            - Numero de la piece (regroupe les lignes)" },
    { text: "4. Compte            - 'Code Libelle' (ex: 411100 Clients)" },
    { text: "5. Partenaire        - Nom du tiers (creation auto client/fournisseur)" },
    { text: "6. Libellé           - Description de la ligne" },
    { text: "7. Débit / Crédit    - Montants" },
    { text: "8. Échéance          - Date d'échéance (pour les factures)" },
    { text: "9. Solde             - Restant du (suivi des impayés)" },
    { text: "" },
    { text: "CREATION AUTOMATIQUE :", bold: true, size: 12, color: "009688" },
    { text: "  - Compte 401xxx + Partenaire -> Fournisseur (401xxxxx)" },
    { text: "  - Compte 411xxx + Partenaire -> Client (411xxxxx)" },
    { text: "  - Compte 521xxx -> Compte de paiement Banque" },
    { text: "  - Compte 571xxx -> Compte de paiement Caisse" },
    { text: "  - Comptes utilises -> crees dans le plan comptable du dossier" },
    { text: "" },
    { text: "PIECES FACTURE :", bold: true, size: 12 },
    { text: "Si le fichier factures n'est pas importe, les pieces INV/ et BILL/" },
    { text: "deviennent automatiquement des Entrées/Sorties avec échéances et" },
    { text: "suivi des impayés (colonne Solde)." },
    { text: "" },
    { text: "REGROUPEMENT : les lignes sont regroupees par Numéro de piece." },
    { text: "L'appariement debit/credit preserve la balance exacte par compte." },
    { text: "" },
    { text: "FORMATS ACCEPTES : .xlsx, .csv", bold: true },
  ]);

  return downloadWorkbook(workbook, "modele_odoo_ecritures_comptables.xlsx");
};

/* ── 2. Factures (account.move) ──────────────────────────────────── */

export const generateMovesTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Factures");

  ws.columns = [
    { header: "Numéro", key: "numero", width: 18 },
    { header: "Partenaire", key: "partenaire", width: 24 },
    { header: "Journal", key: "journal", width: 16 },
    { header: "Date de facturation", key: "date_facturation", width: 20 },
    { header: "Date", key: "date", width: 14 },
    { header: "Échéance", key: "echeance", width: 14 },
    { header: "Montant hors taxes signé dans la devise", key: "montant_ht", width: 24 },
    { header: "Taxe signée", key: "taxe", width: 14 },
    { header: "Total signé en devises", key: "total", width: 18 },
    { header: "Montant dû signé", key: "montant_du", width: 16 },
    { header: "Devise", key: "devise", width: 10 },
    { header: "Statut en cours de paiement", key: "statut", width: 22 },
  ];

  styleHeader(ws);

  addExampleRows(ws, [
    ["INV/2026/0001", "Client Test", "Ventes", "2026-01-15", "2026-01-15", "2026-02-15", 100000, 0, 100000, 0, "XOF", "Payé"],
    ["INV/2026/0002", "Cliente Test 2", "Ventes", "2026-01-20", "2026-01-20", "2026-02-20", 250000, 0, 250000, 100000, "XOF", "Partiellement réglé"],
    ["INV/2026/0003", "Client Test 3", "Ventes", "2026-01-25", "2026-01-25", "2026-02-25", 50000, 0, 50000, 50000, "XOF", "Comptabilisé"],
    ["BILL/2026/0001", "Fournisseur Test", "Achats", "2026-01-10", "2026-01-10", "2026-02-10", -50000, 0, -50000, 0, "XOF", "Payé"],
    ["BILL/2026/0002", "Fournisseur Test 2", "Achats", "2026-01-18", "2026-01-18", "2026-02-18", -80000, -14400, -94400, -94400, "XOF", "Comptabilisé"],
  ]);

  addInstructionsSheet(workbook, "Factures", [
    { text: "MODELE IMPORT FACTURES ODOO (account.move)", bold: true, size: 16, color: "009688" },
    { text: "" },
    { text: "FICHIER OPTIONNEL - liste operationnelle de vos ventes et achats.", bold: true, size: 12 },
    { text: "" },
    { text: "COLONNES (format export Odoo francais) :", bold: true, size: 12 },
    { text: "1. Numéro            - Numero de facture - OBLIGATOIRE" },
    { text: "2. Partenaire        - Nom du client/fournisseur (creation auto)" },
    { text: "3. Journal           - Ventes (facture client) / Achats (fournisseur)" },
    { text: "4. Date de facturation - Date de la facture" },
    { text: "5. Échéance          - Date limite de paiement" },
    { text: "6. Montant hors taxes - HT" },
    { text: "7. Taxe signée       - TVA" },
    { text: "8. Total signé en devises - TTC (montants fournisseurs negatifs : normal)" },
    { text: "9. Montant dû signé  - Restant à payer (suivi des impayés)" },
    { text: "10. Statut en cours de paiement - Payé / Partiellement réglé / Comptabilisé" },
    { text: "" },
    { text: "DETECTION DU TYPE :", bold: true, size: 12, color: "009688" },
    { text: "  - Journal 'Ventes' ou numero INV/  -> ENTREE (facture client)" },
    { text: "  - Journal 'Achats' ou numero BILL/ -> SORTIE (facture fournisseur)" },
    { text: "" },
    { text: "ANTI DOUBLE COMPTAGE :", bold: true, size: 12, color: "c0392b" },
    { text: "Si le fichier ecritures est importe EN MEME TEMPS, les factures ne" },
    { text: "regenerent PAS d'ecritures comptables (deja importees)." },
    { text: "Importez toujours les DEUX fichiers dans la MEME operation." },
    { text: "" },
    { text: "FORMATS ACCEPTES : .xlsx, .csv", bold: true },
  ]);

  return downloadWorkbook(workbook, "modele_odoo_factures.xlsx");
};

/* ── 3. Plan comptable (account.account) ─────────────────────────── */

export const generateAccountsTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Plan comptable");

  ws.columns = [
    { header: "Code", key: "code", width: 14 },
    { header: "Nom du compte", key: "nom", width: 45 },
    { header: "Type", key: "type", width: 22 },
    { header: "Compte parent", key: "parent", width: 16 },
    { header: "Lettrage des paiements", key: "lettrage", width: 18 },
    { header: "Actif", key: "actif", width: 10 },
    { header: "Non commercial", key: "non_commercial", width: 14 },
    { header: "Taxes par défaut", key: "taxes", width: 16 },
    { header: "Étiquettes", key: "etiquettes", width: 14 },
    { header: "Taux actuel", key: "taux", width: 12 },
    { header: "Catégorie fiscale", key: "categorie", width: 18 },
  ];

  styleHeader(ws);

  addExampleRows(ws, [
    ["101100", "Capital engagé non appelé", "Capitaux propres", null, false, true, false, null, null, 0, null],
    ["411100", "Clients", "Créances", null, true, true, false, null, null, 0, null],
    ["401100", "Fournisseurs", "Dettes", null, true, true, false, null, null, 0, null],
    ["521001", "Banque", "Banque", null, false, true, false, null, null, 0, null],
    ["571100", "Argent liquide en monnaie nationale", "Caisse", null, false, true, false, null, null, 0, null],
    ["601100", "Achats de marchandises", "Dépenses", null, false, true, false, null, null, 0, null],
    ["701100", "Vente de marchandises dans la région", "Revenus", null, false, true, false, null, null, 0, null],
  ]);

  addInstructionsSheet(workbook, "Plan comptable", [
    { text: "MODELE IMPORT PLAN COMPTABLE ODOO (account.account)", bold: true, size: 16, color: "009688" },
    { text: "" },
    { text: "FICHIER OPTIONNEL - importe votre plan comptable complet.", bold: true, size: 12 },
    { text: "" },
    { text: "COLONNES (format export Odoo francais) :", bold: true, size: 12 },
    { text: "1. Code          - Numero du compte - OBLIGATOIRE (ex: 411100)" },
    { text: "2. Nom du compte - Libelle du compte - OBLIGATOIRE" },
    { text: "Les autres colonnes sont informatives (ignorees par l'import)." },
    { text: "" },
    { text: "COMPORTEMENT :", bold: true, size: 12, color: "009688" },
    { text: "  - Chaque compte absent est cree dans le plan du dossier" },
    { text: "  - Les comptes deja existants (meme code) ne sont pas modifies" },
    { text: "  - Seules les colonnes Code et Nom du compte sont utilisees" },
    { text: "" },
    { text: "NOTE : meme sans ce fichier, les comptes UTILISES dans les ecritures" },
    { text: "sont crees automatiquement. Ce fichier importe le plan COMPLET." },
    { text: "" },
    { text: "FORMATS ACCEPTES : .xlsx, .csv", bold: true },
  ]);

  return downloadWorkbook(workbook, "modele_odoo_plan_comptable.xlsx");
};

/* ── 4. Contacts (res.partner) ───────────────────────────────────── */

export const generatePartnersTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Contacts");

  ws.columns = [
    { header: "Créé le", key: "cree_le", width: 18 },
    { header: "Nom d'affichage", key: "nom", width: 26 },
    { header: "N° TVA", key: "tva", width: 14 },
    { header: "E-mail", key: "email", width: 28 },
    { header: "Téléphone", key: "telephone", width: 16 },
    { header: "Rue", key: "rue", width: 24 },
    { header: "Ville", key: "ville", width: 16 },
    { header: "État", key: "etat", width: 14 },
    { header: "Pays", key: "pays", width: 14 },
    { header: "Montant dû", key: "montant_du", width: 14 },
    { header: "Total en retard", key: "total_retard", width: 14 },
  ];

  styleHeader(ws);

  addExampleRows(ws, [
    ["2026-01-10", "Client Test", null, "client.test@example.sn", "771234567", "12 rue Carnot", "Dakar", null, "Sénégal", 0, 0],
    ["2026-01-12", "Cliente Test 2", null, "cliente2.test@example.sn", "770998877", "Avenue Cheikh Anta Diop", "Dakar", null, "Sénégal", 100000, 0],
    ["2026-01-15", "Fournisseur Test", null, "fournisseur.test@example.sn", "338001020", "Route de Rufisque", "Thiès", null, "Sénégal", 0, 0],
    ["2026-01-20", "Fournisseur Test 2", null, "fournisseur2.test@example.sn", "338001021", "Zone industrielle", "Dakar", null, "Sénégal", 50000, 50000],
  ]);

  addInstructionsSheet(workbook, "Contacts", [
    { text: "MODELE IMPORT CONTACTS ODOO (res.partner)", bold: true, size: 16, color: "009688" },
    { text: "" },
    { text: "FICHIER OPTIONNEL - coordonnees de vos clients et fournisseurs.", bold: true, size: 12 },
    { text: "" },
    { text: "COLONNES (format export Odoo francais) :", bold: true, size: 12 },
    { text: "1. Nom d'affichage - Nom du contact - OBLIGATOIRE" },
    { text: "2. E-mail          - Email (dedoublonnage)" },
    { text: "3. Téléphone       - Telephone" },
    { text: "4. Rue / Ville / Pays - Adresse" },
    { text: "" },
    { text: "COMPORTEMENT :", bold: true, size: 12, color: "009688" },
    { text: "Les contacts sont importes EN DERNIER : si le tiers existe deja" },
    { text: "(cree par les ecritures ou factures), il est ENRICHI avec" },
    { text: "l'email, le telephone et l'adresse - pas de doublon." },
    { text: "" },
    { text: "DIFFERENCIATION CLIENT / FOURNISSEUR :", bold: true, size: 12 },
    { text: "  - Si importe avec les ecritures : le type vient des ecritures" },
    { text: "    (compte 401xxx -> Fournisseur, 411xxx -> Client)" },
    { text: "  - Sinon : customer_rank / supplier_rank si presents, sinon" },
    { text: "    type_contact, sinon client par defaut" },
    { text: "" },
    { text: "FORMATS ACCEPTES : .xlsx, .csv", bold: true },
  ]);

  return downloadWorkbook(workbook, "modele_odoo_contacts.xlsx");
};

/* ── 5. Journaux (account.journal) - conserve pour compatibilite ─── */

export const generateJournalsTemplate = async () => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Journaux");

  ws.columns = [
    { header: "Nom du journal", key: "name", width: 30 },
    { header: "Code", key: "code", width: 15 },
    { header: "Type", key: "type", width: 18 },
  ];

  styleHeader(ws);

  addExampleRows(ws, [
    { name: "Banque BAO", code: "BANK", type: "Banque" },
    { name: "Caisse principale", code: "CASH", type: "Caisse" },
  ]);

  addInstructionsSheet(workbook, "Journaux", [
    { text: "MODELE IMPORT JOURNAUX ODOO (account.journal)", bold: true, size: 16, color: "009688" },
    { text: "" },
    { text: "Types acceptes : Banque (bank), Caisse (cash)." },
    { text: "Chaque journal devient un Compte de paiement dans le dossier." },
    { text: "Note : les comptes de paiement sont aussi crees automatiquement" },
    { text: "depuis les ecritures (comptes 521xxx et 571xxx)." },
  ]);

  return downloadWorkbook(workbook, "modele_odoo_journaux.xlsx");
};
